"use client"

import { useState, useEffect } from "react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import "./UploadSection.css"
import logo from "./logo.svg"
import config from "./config.json"
import { FaUserCircle } from "react-icons/fa"
import { FiChevronDown } from "react-icons/fi"
import { FcGoogle } from "react-icons/fc"

// Registration function
async function registerUser(userData) {
  try {
    const response = await fetch(`${config.AUTH_BASE_URL}/auth/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(userData),
    })

    const data = await response.json() // Parse response JSON

    // Update the success condition to check for status 200
    if (data.status === 200) {
      return { success: true, data } // Successful registration
    } else if (data.status === 400 && data.message === "User already exist") {
      return { success: false, error: "User already exists" } // Return user already exists error
    } else {
      throw new Error(`${data.message}`)
    }
  } catch (error) {
    return { success: false, error: error.message } // Return error object
  }
}

async function login(username, password) {
  try {
    const response = await fetch(`${config.AUTH_BASE_URL}/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: username,
        password: password,
        product_name: "MAYAMAYA_LITE",
      }),
    })

    if (response.ok) {
      const data = await response.json()
      if (data.access_token) {
        localStorage.setItem("access_token", data.access_token)
        localStorage.setItem("email", username)
        return data // Login successful
      } else {
        return null
      }
    } else if (response.status === 401) {
      return null
    } else {
      throw new Error(`Login failed: ${response.statusText}`)
    }
  } catch (error) {
    return null
  }
}

// New function to handle forgot password
async function handleForgotPassword(email) {
  try {
    const response = await fetch(`${config.AUTH_BASE_URL}/api/user/send-mail/forgot-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: email,
        suite_product: "RESUME-FORENSICS",
      }),
    })

    const data = await response.json()
    return data // Return the response data
  } catch (error) {
    console.error("Error sending forgot password email:", error)
    return null
  }
}

const scrollToSection = (sectionId) => {
  const currentPath = window.location.pathname
  if (currentPath === "/") {
    const section = document.getElementById(sectionId)
    if (section) {
      section.scrollIntoView({ behavior: "smooth" })
    }
  } else {
    window.location.href = `/#${sectionId}`
  }
}

function Header({
  name,
  setIsRegisterModalOpen,
  setIsModalOpen,
  isModalOpen,
  isRegisterModalOpen,
  setIsLoggedIn,
  setUsername,
  setAccessKey,
  remainingCredits,
}) {
  const [isLoggedIn, setIsLoggedInLocal] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const [username, setUsernameLocal] = useState("") // For login
  const [email, setEmail] = useState("") // For registration
  const [password, setPassword] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [orgName, setOrgName] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [popupMessage, setPopupMessage] = useState("")
  const [showPopup, setShowPopup] = useState(false)
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("")
  const [googleUserData, setGoogleUserData] = useState(null)
  const [showGoogleRegisterForm, setShowGoogleRegisterForm] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const storedEmail = localStorage.getItem("email")
    const storedAccessToken = localStorage.getItem("access_token")

    if (storedEmail && storedAccessToken) {
      setIsLoggedIn(true)
      setIsLoggedInLocal(true)
      setUsername(storedEmail)
      setAccessKey(storedAccessToken) // Set access token if available
    }
  }, [setIsLoggedIn, setAccessKey])

  const handleLogout = () => {
    setIsLoggedIn(false)
    setIsLoggedInLocal(false)
    setUsername("") // Clear username on logout
    setAccessKey("") // Clear access key on logout
    localStorage.removeItem("access_token") // Remove access token from localStorage
    localStorage.removeItem("email")
    localStorage.removeItem("uploadedFiles") // Remove uploaded files from localStorage
    localStorage.removeItem("driveLink")
    window.location.href = "/" // Redirect to homepage
  }

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen)
  }

  // Toggle password visibility
  const toggleShowPassword = () => {
    setShowPassword(!showPassword)
  }

  const handleLogin = async () => {
    const response = await login(username, password)
    if (response) {
      // Only set logged-in state if response is valid
      setIsLoggedIn(true)
      setIsLoggedInLocal(true)
      setUsername(username)
      setAccessKey(response.access_token) // Assuming access token is used as access key
      setIsModalOpen(false)
      navigate("/dashboard")
      //window.location.reload();
    } else {
      // If response is null or there's an error
      setIsLoggedIn(false)
      setIsLoggedInLocal(false)
      triggerPopup("Login failed") // This alert can be improved based on your UX design
    }
  }

  const handleRegister = async () => {
    if (!firstName || !lastName || !email || !password) {
      triggerPopup("Please fill in all required fields.")
      return
    }

    const response = await registerUser({
      email: email,
      password: password,
      first_name: firstName,
      last_name: lastName,
      org_name: orgName,
      product_name: "MAYAMAYA_LITE",
    })

    if (response.success) {
      triggerPopup("Registration successful!")
      setIsRegisterModalOpen(false)

      // Immediately attempt login using the same credentials
      const loginResponse = await login(email, password) // Use email and password directly for login
      if (loginResponse) {
        //console.log("Login successful after registration");
        setIsLoggedIn(true)
        setIsLoggedInLocal(true)
        setUsername(email) // Set the username to email
        setAccessKey(loginResponse.access_token) // Assuming access token is used as access key
        setIsModalOpen(false)
        navigate("/dashboard")
      } else {
        triggerPopup("Login failed after registration. Please try again.")
      }
    } else {
      triggerPopup(`Registration failed: ${response.error}`)
    }
  }

  const triggerPopup = (message) => {
    setPopupMessage(message)
    setShowPopup(true)
    setTimeout(() => {
      setShowPopup(false)
    }, 3000) // Display the popup for 3 seconds
  }

  const handleClosePopup = () => {
    setShowPopup(false)
    setPopupMessage("") // Optionally clear the popup message if needed
  }

  const handleForgotPasswordClick = async () => {
    if (!username) {
      triggerPopup("Please enter your email address.")
      return
    }

    const response = await handleForgotPassword(username) // Use username as email
    if (response) {
      triggerPopup("Forgot password email sent! Check your inbox.")
      setUsername("") // Clear the email input
    } else {
      triggerPopup("Failed to send forgot password email.")
    }
  }

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen)
  }

  const handleProfile = () => {
    navigate("/profile") // Navigate to profile page
  }

  const handleBillings = () => {
    navigate("/pricing") // Navigate to pricing/billings page
  }

  const toggleMenu = () => {
    setMenuOpen(!menuOpen)
  }

  const handleLogo = () => {
    navigate("/")
  }

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
      if (window.innerWidth > 768) {
        setMenuOpen(false) // Automatically close the menu on larger screens
      }
    }

    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  const handleGoogleLogin = async () => {
    try {
      const response = await fetch(`${config.AUTH_BASE_URL}/authorize/google/signin`, {
        headers: {
          "Access-Control-Allow-Origin": "* ",
          "Access-Control-Allow-Headers": "Content-Type",
        },
      })
      const data = await response.json()
      if (response.ok && data.data && data.data.auth_url) {
        window.location.assign(data.data.auth_url)
      } else {
        triggerPopup("Google login failed")
      }
    } catch (error) {
      console.error("Error during Google login:", error)
      triggerPopup("An error occurred during Google login")
    }
  }

  const handleGoogleRegister = async () => {
    try {
      const response = await fetch(`${config.AUTH_BASE_URL}/authorize/google/signup`, {
        headers: {
          "Access-Control-Allow-Origin": "* ",
          "Access-Control-Allow-Headers": "Content-Type",
        },
      })
      const data = await response.json()
      if (response.ok && data.data && data.data.auth_url) {
        window.location.assign(data.data.auth_url)
      } else {
        triggerPopup("Google registration failed")
      }
    } catch (error) {
      console.error("Error during Google registration:", error)
      triggerPopup("An error occurred during Google registration")
    }
  }

  useEffect(() => {
    const handleRedirect = () => {
      const urlParams = new URLSearchParams(window.location.search)
      const accessToken = urlParams.get("access_token")
      const email = urlParams.get("email")
      const firstName = urlParams.get("first_name")
      const lastName = urlParams.get("last_name")
      const isNewUser = urlParams.get("new_user") === "True"

      if (accessToken && email) {
        localStorage.setItem("access_token", accessToken)
        localStorage.setItem("email", email)

        if (isNewUser) {
          // It's a new user (signup)
          setGoogleUserData({ firstName, lastName, email })
          setShowGoogleRegisterForm(true)
          setIsRegisterModalOpen(true)
        } else {
          // It's an existing user (login)
          setIsLoggedIn(true)
          setIsLoggedInLocal(true)
          setUsername(email)
          setAccessKey(accessToken)
          setIsModalOpen(false)
          navigate("/dashboard")
        }

        // Clear the URL parameters
        window.history.replaceState({}, document.title, "/")
      }
    }

    handleRedirect()
  }, [])

  const handleCompleteGoogleRegister = async () => {
    if (!orgName) {
      triggerPopup("Please enter your organization name")
      return
    }
    const accessToken = localStorage.getItem("access_token")
    const email = localStorage.getItem("email")
    await updateOrgDetails(email, orgName, accessToken)
    setIsLoggedIn(true)
    setIsLoggedInLocal(true)
    setUsername(email)
    setAccessKey(accessToken)
    setIsRegisterModalOpen(false)
    setShowGoogleRegisterForm(false)
    setGoogleUserData(null)
    navigate("/dashboard")
  }

  // Add this new function to update organization details
  const updateOrgDetails = async (email, orgName, accessToken) => {
    try {
      const response = await fetch(`${config.AUTH_BASE_URL}/api/user/update/org-details`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          user_email: email,
          org_name: orgName,
          product_name: "MAYAMAYA_LITE",
        }),
      })

      if (!response.ok) {
        throw new Error("Failed to update organization details")
      }

      triggerPopup("Organization details updated successfully")
    } catch (error) {
      console.error("Error updating organization details:", error)
      triggerPopup("Failed to update organization details")
    }
  }

  useEffect(() => {
    if (location.hash) {
      const sectionId = location.hash.slice(1)
      setTimeout(() => {
        const section = document.getElementById(sectionId)
        if (section) {
          section.scrollIntoView({ behavior: "smooth" })
        }
      }, 0)
    }
  }, [location])

  return (
    <>
      <div className="upload-header">
        {/* Logo */}
        <img src={logo || "/placeholder.svg"} alt="Logo" className="m-logo" />

        {/* Remaining Credits */}
        {isLoggedIn && isMobile && (
          <div className="credits-info">
            Credits: <span>{remainingCredits}</span>
          </div>
        )}

        {!isMobile && (
          <>
            {isLoggedIn ? (
              <nav className="nav-links">
                <Link to="/dashboard" className={`nav-link ${location.pathname === "/dashboard" ? "active" : ""}`}>
                  HOME
                </Link>
                <Link
                  to="/job-description-page"
                  className={`nav-link ${location.pathname === "/job-description-page" ? "active" : ""}`}
                >
                  JOB ROLE
                </Link>
                <Link
                  to="/folder-manager"
                  className={`nav-link ${location.pathname === "/folder-manager" ? "active" : ""}`}
                >
                  RESUMES
                </Link>
                <Link to="/history" className={`nav-link ${location.pathname === "/history" ? "active" : ""}`}>
                  RESULTS
                </Link>
                {/*<Link to="/mayamaya-ai" className={`nav-link ${location.pathname === "/mayamaya-ai" ? "active" : ""}`}>
                  MAYAMAYA AI
                </Link>*/}
              </nav>
            ) : (
              <nav className="nav-links">
                <a
                  href="#how-it-works"
                  className={`nav-link ${location.hash === "#how-it-works" ? "active" : ""}`}
                  onClick={(e) => {
                    e.preventDefault()
                    scrollToSection("how-it-works")
                  }}
                >
                  HOW IT WORKS
                </a>
                <a
                  href="#benefits"
                  className={`nav-link ${location.hash === "#benefits" ? "active" : ""}`}
                  onClick={(e) => {
                    e.preventDefault()
                    scrollToSection("benefits")
                  }}
                >
                  THE BENEFITS
                </a>
                <a
                  href="#pricing"
                  className={`nav-link ${location.hash === "#pricing" ? "active" : ""}`}
                  onClick={(e) => {
                    e.preventDefault()
                    scrollToSection("pricing")
                  }}
                >
                  PRICING
                </a>
              </nav>
            )}
            {/* Remaining Credits */}
            {isLoggedIn && (
              <div className="credits-info">
                Credits: <span>{remainingCredits}</span>
              </div>
            )}
            <div className="login-register-buttons">
              {!isLoggedIn && (
                <button className="register-btn" onClick={isLoggedIn ? handleProfile : () => navigate("/signup")}>
                  SIGNUP
                </button>
              )}

              {!isLoggedIn && (
                <button className="login-btn" onClick={isLoggedIn ? handleLogout : () => navigate("/login")}>
                  LOGIN
                </button>
              )}
            </div>
            {isLoggedIn && (
              <>
                <div className="prof" onClick={toggleMenu}>
                  {/*<span className={`hamburger-icon ${menuOpen ? 'open' : ''}`}></span>*/}
                  <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <FaUserCircle style={{ fontSize: "24px" }} />
                    {name}
                    <FiChevronDown />
                  </span>
                </div>
                {menuOpen && (
                  <nav className="mobile-menu">
                    <ul>
                      <>
                        <li>
                          <a href="/profile">My Profile</a>
                        </li>
                        <li>
                          <a href="/" onClick={handleLogout}>
                            Logout
                          </a>
                        </li>
                      </>
                    </ul>
                  </nav>
                )}
              </>
            )}
          </>
        )}

        <>
          {isMobile && (
            <>
              <div className="hamburger" onClick={toggleMenu}>
                <span className={`hamburger-icon ${menuOpen ? "open" : ""}`}></span>
              </div>
              {menuOpen && (
                <nav className="mobile-menu">
                  <ul>
                    {!isLoggedIn ? (
                      <>
                        <li>
                          <a
                            href="#how-it-works"
                            onClick={(e) => {
                              e.preventDefault()
                              scrollToSection("how-it-works")
                              toggleMenu()
                            }}
                          >
                            How It Works
                          </a>
                        </li>
                        <li>
                          <a
                            href="#benefits"
                            onClick={(e) => {
                              e.preventDefault()
                              scrollToSection("benefits")
                              toggleMenu()
                            }}
                          >
                            The Benefits
                          </a>
                        </li>
                        <li>
                          <a
                            href="#pricing"
                            onClick={(e) => {
                              e.preventDefault()
                              scrollToSection("pricing")
                              toggleMenu()
                            }}
                          >
                            Pricing
                          </a>
                        </li>
                        <li>
                          <p
                            onClick={() => {
                              navigate("/login")
                              toggleMenu()
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            Login
                          </p>
                        </li>
                        <li>
                          <p
                            onClick={() => {
                              navigate("/signup")
                              toggleMenu()
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            Register
                          </p>
                        </li>
                      </>
                    ) : (
                      <>
                        <li>
                          <a href="/dashboard">Home</a>
                        </li>
                        <li>
                          <a href="/job-description-page">Job Role</a>
                        </li>
                        <li>
                          <a href="/folder-manager">Resumes</a>
                        </li>
                        <li>
                          <a href="/history">Results</a>
                        </li>
                        {/*<li>
                          <a href="/mayamaya-ai">MayaMaya AI</a>
                        </li>*/}
                        <li>
                          <a href="/profile">My Profile</a>
                        </li>
                        <li>
                          <a href="/" onClick={handleLogout}>
                            Logout
                          </a>
                        </li>
                      </>
                    )}
                  </ul>
                </nav>
              )}
            </>
          )}
        </>
      </div>
      <section>
        {showPopup && (
          <div>
            <div className="overlay" onClick={handleClosePopup}></div>
            <div className="popup">
              <button className="close-btn" onClick={handleClosePopup}>
                ×
              </button>
              {popupMessage}
            </div>
          </div>
        )}

        {/* Login Modal */}
        {isModalOpen && (
          <div className="modal">
            <div className="modal-content">
              <div className="modal-left">
                <h3>Welcome!</h3>
                <p>Please login to continue.</p>
              </div>
              <div className="modal-right">
                <button className="close-btn" onClick={() => setIsModalOpen(false)}>
                  X
                </button>
                <h3>Login</h3>
                <input
                  type="text"
                  placeholder="Email"
                  value={username}
                  onChange={(e) => setUsernameLocal(e.target.value)}
                />

                <div className="password-input">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                <button className="modal-btn" onClick={handleLogin}>
                  Login
                </button>
                <div>
                  <p style={{ marginTop: "15px", fontSize: "14px", color: "#555" }}>
                    Forgot your password?{" "}
                    <span onClick={handleForgotPasswordClick} style={{ cursor: "pointer", color: "#007bff" }}>
                      Click here
                    </span>
                  </p>
                </div>
                <p style={{ marginTop: "15px", fontSize: "14px", color: "#555" }}>
                  Don't have an account?
                  <a
                    href="/login"
                    style={{ color: "#007bff", textDecoration: "none" }}
                    onMouseOver={(e) => (e.currentTarget.style.textDecoration = "underline")}
                    onMouseOut={(e) => (e.currentTarget.style.textDecoration = "none")}
                  >
                    Register here
                  </a>
                </p>
                <div className="google-login-container" style={{ marginTop: "20px" }}>
                  <button onClick={handleGoogleLogin} className="google-login-button">
                    <FcGoogle className="google-icon" />
                    Sign In with Google
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Registration Modal */}
        {isRegisterModalOpen && (
          <div className="modal">
            <div className="modal-content">
              <div className="modal-left">
                <h3>Join Us!</h3>
                <p>Create your account and start your journey.</p>
              </div>
              <div className="modal-right">
                <button
                  className="close-btn"
                  onClick={() => {
                    setIsRegisterModalOpen(false)
                    setShowGoogleRegisterForm(false)
                    setGoogleUserData(null)
                  }}
                >
                  X
                </button>
                <h3>Register</h3>
                {!showGoogleRegisterForm ? (
                  <>
                    {/* Regular registration form */}
                    <input
                      type="text"
                      placeholder="First Name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Last Name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                    <input type="text" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
                    <div className="password-input">
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Organization Name"
                      value={orgName}
                      onChange={(e) => setOrgName(e.target.value)}
                    />
                    <button className="modal-btn" onClick={handleRegister}>
                      Register
                    </button>
                    <p style={{ marginTop: "15px", fontSize: "14px", color: "#555" }}>
                      Already have an account?
                      <a
                        href="#"
                        onClick={() => {
                          setIsRegisterModalOpen(false)
                          setIsModalOpen(true)
                        }}
                        style={{ color: "#007bff", textDecoration: "none" }}
                        onMouseOver={(e) => (e.currentTarget.style.textDecoration = "underline")}
                        onMouseOut={(e) => (e.currentTarget.style.textDecoration = "none")}
                      >
                        Login here
                      </a>
                    </p>
                    <div className="google-login-container" style={{ marginTop: "20px" }}>
                      <button onClick={handleGoogleRegister} className="google-login-button">
                        <FcGoogle className="google-icon" /> Sign Up with Google
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <h3>Complete Your Registration</h3>
                    <input type="text" placeholder="First Name" value={googleUserData.firstName} readOnly />
                    <input type="text" placeholder="Last Name" value={googleUserData.lastName} readOnly />
                    <input type="text" placeholder="Email" value={googleUserData.email} readOnly />
                    <input
                      type="text"
                      placeholder="Organization Name"
                      value={orgName}
                      onChange={(e) => setOrgName(e.target.value)}
                    />
                    <button className="modal-btn" onClick={handleCompleteGoogleRegister}>
                      Complete Registration
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  )
}

export default Header

